/**
 * KanbanColumn — a single drop-zone column.
 *
 * Per FWV v8 §30: rendering extracted from TodayView. Uses @dnd-kit's
 * useDroppable so cards can be dragged onto it.
 *
 * Drop zone visuals:
 *  - Idle: subtle border-top accent, white bg
 *  - isOver + has cards: Notion-style insertion line between cards (computed from
 *    the drag cursor's Y position vs each card's vertical midpoint)
 *  - isOver + empty: solid garden-50 box with "↓ drop here"
 */
import { useDroppable } from '@dnd-kit/core'
import { useEffect, useState } from 'react'
import { KanbanCard } from './KanbanCard'
import type { DbTask } from '../../db'

export function KanbanColumn({
  status,
  label,
  tone,
  count,
  tasks,
  isSelected,
  activeDragId,
  dragCursorY,
  onCardClick,
  onCardAdvance,
  onInsertionChange,
}: {
  status: DbTask['status']
  label: string
  tone: string
  count: number
  tasks: DbTask[]
  isSelected: (id: string) => boolean
  activeDragId: string | null
  dragCursorY: number | null
  onCardClick: (e: React.MouseEvent, task: DbTask) => void
  onCardAdvance: (task: DbTask) => void
  onInsertionChange: (index: number | null) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    data: { status },
  })

  const [insertionIndex, setInsertionIndex] = useState<number | null>(null)

  // Compute the insertion line position: which gap between cards is closest
  // to the cursor's Y coordinate. Notion/Linear-style.
  useEffect(() => {
    if (!isOver || dragCursorY === null || tasks.length === 0) {
      setInsertionIndex(null)
      onInsertionChange(null)
      return
    }
    // Find the gap closest to cursor Y. Cards have a data-card-id attribute.
    // Walk the rendered cards in order and find the first card whose midpoint
    // is below the cursor → insert before that card. Otherwise insert at end.
    const cards = document.querySelectorAll(
      `[data-status="${status}"] [data-card-id]`
    )
    let idx = tasks.length // default: insert at end
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i].getBoundingClientRect()
      const mid = r.top + r.height / 2
      if (dragCursorY < mid) {
        idx = i
        break
      }
    }
    setInsertionIndex(idx)
    onInsertionChange(idx)
  }, [isOver, dragCursorY, status, tasks.length, onInsertionChange])

  const isEmpty = tasks.length === 0
  const isDragging = activeDragId !== null
  const showStrongDrop = isDragging && isEmpty

  return (
    <div
      ref={setNodeRef}
      data-status={status}
      data-drop-active={isOver ? 'true' : undefined}
      className={`relative bg-white border-t-4 ${tone} border rounded-2xl p-3 min-h-[200px] transition-colors duration-200 ${
        isOver
          ? 'border-garden-500 bg-garden-50/40'
          : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-sm font-medium text-skin-text flex items-center gap-1.5">
          <span aria-hidden="true" className="text-slate-300 select-none" title="Sorted by position">≡</span>
          {label}
        </h2>
        <span className={`text-xs rounded-full px-2 py-0.5 transition-colors ${
          isOver ? 'bg-garden-200 text-garden-800' : 'bg-slate-100 text-slate-400'
        }`}>{count}</span>
      </div>
      <div className="space-y-2 min-h-[60px]">
        {tasks.map((task, i) => (
          <div key={task.id}>
            {/* Notion-style insertion line ABOVE this card */}
            {isOver && insertionIndex === i && (
              <div className="h-1 my-1 rounded-full bg-garden-500 shadow-[0_0_0_2px_rgba(109,40,217,0.15)] animate-pulse" />
            )}
            <KanbanCard
              task={task}
              isSelected={isSelected(task.id)}
              isDragging={activeDragId === task.id}
              onCardClick={onCardClick}
              onAdvance={onCardAdvance}
            />
          </div>
        ))}
        {/* Notion-style insertion line at the END */}
        {isOver && insertionIndex === tasks.length && tasks.length > 0 && (
          <div className="h-1 my-1 rounded-full bg-garden-500 shadow-[0_0_0_2px_rgba(109,40,217,0.15)] animate-pulse" />
        )}
        {isEmpty && (
          <div className={`text-xs italic px-1 py-6 text-center rounded-xl border-2 border-dashed transition-colors ${
            showStrongDrop
              ? 'border-garden-500 text-garden-700 bg-garden-50'
              : 'border-slate-200 text-slate-400'
          }`}>
            {showStrongDrop ? '↓ drop here' : '—'}
          </div>
        )}
      </div>
    </div>
  )
}